import React, { useEffect, useRef } from "react";
import { colors } from '../../theme/colors';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Fade,
  keyframes,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import confetti from "canvas-confetti";

const getDialogType = (title = "", message = "") => {
  const t = title.toLowerCase();
  if (t.includes("completed") || t.includes("success") || t.includes("thank"))
    return "success";
  if (t.includes("cancel") || message.toLowerCase().includes("cancelled"))
    return "error";
  if (t.includes("expired") || message.toLowerCase().includes("expired"))
    return "warning";
  if (t.includes("invalid") || message.toLowerCase().includes("invalid"))
    return "info";
  return "info";
};

const InterviewStatusDialogue = ({
  open,
  message = "",
  title = "Interview Submitted",
  handleStopStream = () => {},
}) => {
  const confettiRef = useRef(null);
  const dialogType = getDialogType(title, message);

  useEffect(() => {
    if (open && confettiRef.current && dialogType === "success") {
      const myConfetti = confetti.create(confettiRef.current, {
        resize: true,
        useWorker: true,
      });

      myConfetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
      });
    }
  }, [open, dialogType]);

  const fadeInOut = keyframes`
    0% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 1; }
  `;

  let icon, iconBg, iconColor, mainColor;
  if (dialogType === "success") {
    icon = <DoneIcon sx={{ fontSize: "3.5rem", color: "#fff" }} />;
    iconBg = "linear-gradient(135deg, #43a047 0%, #66bb6a 100%)";
    iconColor = "#43a047";
    mainColor = "#43a047";
  } else if (dialogType === "error") {
    icon = <ErrorOutlineIcon sx={{ fontSize: "3.5rem", color: "#fff" }} />;
    iconBg = "linear-gradient(135deg, #e53935 0%, #ff7043 100%)";
    iconColor = "#e53935";
    mainColor = "#e53935";
  } else if (dialogType === "warning") {
    icon = <WarningAmberIcon sx={{ fontSize: "3.5rem", color: "#fff" }} />;
    iconBg = "linear-gradient(135deg, #fbc02d 0%, #ffb300 100%)";
    iconColor = "#fbc02d";
    mainColor = "#fbc02d";
  } else {
    icon = <InfoOutlinedIcon sx={{ fontSize: "3.5rem", color: "#fff" }} />;
    iconBg = `linear-gradient(135deg, ${colors.secondary} 0%, #f87171 100%)`;
    iconColor = colors.secondary;
    mainColor = colors.secondary;
  }

  return (
    <>
      {dialogType === "success" && (
        <canvas
          ref={confettiRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
            zIndex: 2000,
          }}
        />
      )}

      <Dialog
        open={open}
        TransitionComponent={Fade}
        keepMounted
        aria-labelledby="interview-submit-dialog-title"
        aria-describedby="interview-submit-dialog-description"
        sx={{
          "& .MuiBackdrop-root": {
            animation: `${fadeInOut} 2.5s ease-in-out forwards`,
            backdropFilter: "blur(40px)", // 4px
            background: "rgba(0, 0, 0, 0.4)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 6,
            px: { xs: 2, sm: 6 },
            py: { xs: 3, sm: 6 },
            background: "#fff",
            color: "#222",
            minWidth: { xs: "90vw", sm: "400px" },
            boxShadow: `0 8px 32px ${mainColor}33`,
            border: `1.5px solid ${mainColor}22`,
            animation: `${fadeInOut} 2.5s ease-in-out forwards`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: iconBg,
              boxShadow: `0 0 0 0 ${iconColor}, 0 0 32px 0 ${iconColor}44`,
              width: 100,
              height: 100,
              mb: 2,
              position: "relative",
              animation:
                dialogType === "success"
                  ? "pulseGlow 1.8s infinite ease-in-out"
                  : "none",
              "@keyframes pulseGlow": {
                "0%": { boxShadow: `0 0 0 0 ${iconColor}88` },
                "70%": { boxShadow: `0 0 0 18px ${iconColor}00` },
                "100%": { boxShadow: `0 0 0 0 ${iconColor}00` },
              },
            }}
          >
            {icon}
          </Box>
          <DialogTitle
            sx={{
              fontWeight: 800,
              fontSize: "2rem",
              color: "text.secondary",
              textAlign: "center",
              letterSpacing: 1,
              mb: 0,
              p: 0,
            }}
          >
            {title}
          </DialogTitle>
          <Typography
            variant="subtitle1"
            sx={{
              color: mainColor,
              fontWeight: 600,
              textAlign: "center",
              mb: 1,
              letterSpacing: 0.5,
            }}
          >
            {dialogType === "success"
              ? "Thank you for your submission!"
              : dialogType === "error"
              ? "Interview Cancelled"
              : dialogType === "warning"
              ? "Link Expired"
              : dialogType === "info"
              ? "Invalid Link"
              : ""}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: "2px",
            background: `linear-gradient(90deg, ${mainColor}22 0%, #fff 100%)`,
            borderRadius: 1,
            mb: 2,
          }}
        />
        <DialogContent sx={{ p: 0 }}>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mt: 1,
              color: "#333",
              fontSize: "1.08rem",
              fontWeight: 500,
            }}
          >
            {message ||
              (dialogType === "success"
                ? "Thank you for completing the interview. Your responses have been submitted successfully."
                : dialogType === "error"
                ? "This interview has been cancelled. Please contact your administrator for more information."
                : dialogType === "warning"
                ? "This link has expired. Please contact your administrator for a new link."
                : dialogType === "info"
                ? "This link is invalid. Please check the link or contact support."
                : "")}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InterviewStatusDialogue;
